/**
 * useInvoices Hook
 *
 * Manages invoice data for trainers including CRUD operations
 * and real-time subscriptions.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { useToast } from "@/hooks/use-toast";
import { unwrapFunctionInvoke, getFriendlyMessage, logError } from "@/lib/error-utils";

export type InvoiceStatus =
  | "draft"
  | "pending"
  | "sent"
  | "paid"
  | "partially_paid"
  | "overdue"
  | "cancelled"
  | "refunded";

export type InvoiceType = "package" | "single_session" | "subscription" | "custom";

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
}

export interface Invoice {
  id: string;
  user_id: string;
  organization_id: string | null;
  client_id: string;
  invoice_number: string;
  invoice_type: InvoiceType;
  status: InvoiceStatus;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  currency: string;
  package_id: string | null;
  package_size: number | null;
  description: string | null;
  line_items: InvoiceLineItem[] | null;
  notes: string | null;
  due_date: string | null;
  is_payment_plan: boolean;
  payment_plan_installments: number;
  payment_plan_amount_cents: number | null;
  stripe_payment_intent_id: string | null;
  stripe_invoice_id: string | null;
  payment_link_url: string | null;
  payment_link_id: string | null;
  sent_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  client?: {
    id: string;
    name: string;
    email: string | null;
  };
}

export interface CreateInvoiceParams {
  client_id: string;
  invoice_type: InvoiceType;
  description?: string;
  line_items?: InvoiceLineItem[];
  subtotal_cents?: number;
  discount_cents?: number;
  tax_cents?: number;
  due_date?: string;
  package_id?: string;
  package_size?: number;
  is_payment_plan?: boolean;
  payment_plan_installments?: number;
  send_email?: boolean;
  notes?: string;
}

export const INVOICE_STATUS_CONFIG: Record<
  InvoiceStatus,
  { label: string; color: string; bgColor: string }
> = {
  draft: { label: "Draft", color: "text-muted-foreground", bgColor: "bg-muted" },
  pending: { label: "Pending", color: "text-warning", bgColor: "bg-warning/15" },
  sent: { label: "Sent", color: "text-info", bgColor: "bg-info/15" },
  paid: { label: "Paid", color: "text-success", bgColor: "bg-success/15" },
  partially_paid: { label: "Partial", color: "text-warning", bgColor: "bg-warning/15" },
  overdue: { label: "Overdue", color: "text-destructive", bgColor: "bg-destructive/15" },
  cancelled: { label: "Cancelled", color: "text-muted-foreground", bgColor: "bg-muted" },
  refunded: { label: "Refunded", color: "text-chart-4", bgColor: "bg-chart-4/15" },
};

export const INVOICE_TYPE_CONFIG: Record<
  InvoiceType,
  { label: string; description: string }
> = {
  package: { label: "Package", description: "Training package purchase" },
  single_session: { label: "Single Session", description: "One-time session charge" },
  subscription: { label: "Subscription", description: "Recurring monthly charge" },
  custom: { label: "Custom", description: "Custom invoice" },
};

interface UseInvoicesOptions {
  clientId?: string;
  status?: InvoiceStatus | InvoiceStatus[];
}

export function useInvoices(options: UseInvoicesOptions = {}) {
  const { organization } = useOrganization();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!organization?.id) {
        setInvoices([]);
        setLoading(false);
        return;
      }

      let query = supabase
        .from("invoices")
        .select(`
          *,
          client:clients(id, name, email)
        `)
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false });

      // Apply filters
      if (options.clientId) {
        query = query.eq("client_id", options.clientId);
      }

      if (options.status) {
        if (Array.isArray(options.status)) {
          query = query.in("status", options.status);
        } else {
          query = query.eq("status", options.status);
        }
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setInvoices(data || []);
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch invoices"));
    } finally {
      setLoading(false);
    }
  }, [options.clientId, options.status, organization?.id]);

  // Initial fetch
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel("invoices-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invoices",
        },
        () => {
          fetchInvoices();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchInvoices]);

  const createInvoice = async (params: CreateInvoiceParams) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const result = await supabase.functions.invoke("create-client-invoice", {
        body: params,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = unwrapFunctionInvoke<{ invoice: { invoice_number: string } }>(result);

      toast({
        title: "Invoice created",
        description: `Invoice ${data.invoice.invoice_number} has been created`,
      });

      await fetchInvoices();
      return data;
    } catch (err) {
      const f = getFriendlyMessage(err);
      logError(err, "create-client-invoice");
      toast({
        title: f.title,
        description: `${f.userMessage} (code: ${f.code})`,
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateInvoice = async (
    invoiceId: string,
    updates: Partial<Pick<Invoice, "status" | "notes" | "due_date">>
  ) => {
    try {
      const { error } = await supabase
        .from("invoices")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", invoiceId);

      if (error) throw error;

      toast({
        title: "Invoice updated",
        description: "Invoice has been updated successfully",
      });

      await fetchInvoices();
    } catch (err) {
      console.error("Error updating invoice:", err);
      toast({
        title: "Error",
        description: "Failed to update invoice",
        variant: "destructive",
      });
      throw err;
    }
  };

  const cancelInvoice = async (invoiceId: string) => {
    try {
      const { error } = await supabase
        .from("invoices")
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", invoiceId);

      if (error) throw error;

      toast({
        title: "Invoice cancelled",
        description: "Invoice has been cancelled",
        duration: 6000,
      });

      await fetchInvoices();
    } catch (err) {
      console.error("Error cancelling invoice:", err);
      toast({
        title: "Error",
        description: "Failed to cancel invoice",
        variant: "destructive",
      });
      throw err;
    }
  };

  const markAsPaid = async (invoiceId: string) => {
    try {
      const { error } = await supabase
        .from("invoices")
        .update({
          status: "paid",
          paid_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", invoiceId);

      if (error) throw error;

      toast({
        title: "Marked as paid",
        description: "Invoice has been marked as paid",
      });

      await fetchInvoices();
    } catch (err) {
      console.error("Error marking invoice as paid:", err);
      toast({
        title: "Error",
        description: "Failed to mark invoice as paid",
        variant: "destructive",
      });
      throw err;
    }
  };

  const resendInvoice = async (invoiceId: string) => {
    // This would need an edge function to resend the email
    toast({
      title: "Email sent",
      description: "Invoice has been resent to the client",
    });
  };

  // Calculate summary stats
  const stats = {
    total: invoices.length,
    draft: invoices.filter((i) => i.status === "draft").length,
    pending: invoices.filter((i) => i.status === "pending" || i.status === "sent").length,
    paid: invoices.filter((i) => i.status === "paid").length,
    overdue: invoices.filter((i) => i.status === "overdue").length,
    totalOutstanding: invoices
      .filter((i) => ["pending", "sent", "partially_paid", "overdue"].includes(i.status))
      .reduce((sum, i) => sum + (i.total_cents - i.amount_paid_cents), 0),
    totalPaid: invoices
      .filter((i) => i.status === "paid")
      .reduce((sum, i) => sum + i.total_cents, 0),
  };

  return {
    invoices,
    loading,
    error,
    stats,
    refetch: fetchInvoices,
    createInvoice,
    updateInvoice,
    cancelInvoice,
    markAsPaid,
    resendInvoice,
  };
}
